import { Component, Injector, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    AnnualReportProvider,
    onUpdateAnnualReportFailed,
    onUpdateAnnualReportSuccessfully,
} from '@modeso/aumico-lib-annualreport-fe';
import { AumicoCoreLayoutProvider, FIRST_ELEMENT_INDEX } from '@modeso/aumico-lib-core-fe';
import {
    AnnualReportStep,
    FixedAssetsSchedule,
    IAccountMappingTemplate,
    IAnnualReport,
    IFinStatements,
} from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import fastDeepEqual from 'fast-deep-equal';
import { cloneDeep } from 'lodash-es';
import { Observable, filter, find, map, of, withLatestFrom } from 'rxjs';
import { MixpanelService } from '../../../../../../mixpanel/mixpanel.service';
import { MIXPANEL_EVENTS } from '../../../../../../mixpanel/mixpanelEvents.enum';
import { DataState } from '../../../shared/enum/datastate.enum';
import { templateConstants } from '../../../shared/template-constants';
import { localizationKeys } from '../../../utils/localizationKeys';
import { AnnualReportSubPage } from '../annual-report-sub-page';
import { PreviousPeriodHelper } from '../helpers/previous-period.helper';

const DATE_OPTIONS: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };

@UntilDestroy()
@Component({
    selector: 'fixed-assets-schedule-page',
    templateUrl: 'fixed-assets-schedule.page.html',
    styleUrls: ['fixed-assets-schedule.page.scss'],
})
export class AnnualReportFixedAssetsSchedulePage extends AnnualReportSubPage implements OnInit {
    localizationKeys = localizationKeys;

    isPartOfArNavigation$: Observable<boolean>;
    dataState: DataState = DataState.Loading;

    showCompleteDataErrorMessage = false;

    private currentAnnualReport: IAnnualReport | undefined;

    fixedAssetsScheduleSavedData: FixedAssetsSchedule | undefined;

    fixedAssetsScheduleToSave: FixedAssetsSchedule | null = null;
    fixedAssetsSchedulePreviousYear: FixedAssetsSchedule | null = null;

    mappedTemplate: IAccountMappingTemplate | undefined;

    balanceOpeningDate: Date | undefined;
    balanceClosingDate: Date;

    // True when the report has no comparison period.
    noPreviousData = false;

    // Start of the current period; only set when there is no comparison period.
    periodStartDate: Date | undefined;

    readonly currentLanguage = this.getCurrentLanguageWithFallBack();

    private isSaveRequestInProgress = false;
    reportLocale: string;

    constructor(
        readonly injector: Injector,
        override readonly dialog: MatDialog,
        public override readonly aumicoCoreLayoutProvider: AumicoCoreLayoutProvider,
        private readonly annualReportProvider: AnnualReportProvider,
        protected override readonly actionsListener$: ActionsSubject,
        private readonly mixpanelService: MixpanelService,
    ) {
        super(injector, actionsListener$, aumicoCoreLayoutProvider, dialog);

        this.currentAnnualReportStep = AnnualReportStep.FAS;

        this.isPartOfArNavigation$ = this.annualReportNavigationProvider.isPartOfArNavigation$(AnnualReportStep.FAS);
    }

    override ngOnInit(): void {
        this.mixpanelService.time_event(MIXPANEL_EVENTS.FAS_TIME_SPENT_TILL_COMPLETION);

        this.disableProceedButton = false;
        this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = false;

        this.dataState = DataState.Loading;

        this.providerOfAnnualReports
            .getActiveAnnualReportEncryptedId$()
            .pipe(
                find((activeAnnualReportEncryptedId) => activeAnnualReportEncryptedId != null),
                untilDestroyed(this),
            )
            .subscribe((activeAnnualReportEncryptedId) => {
                this.providerOfAnnualReports.dispatchActiveAnnualReport(activeAnnualReportEncryptedId);
            });

        this.annualReportProvider
            .getAnnualReportForBalanceAutoFill$()
            .pipe(
                filter((autoFilledAnnualReport: IAnnualReport) => autoFilledAnnualReport != null),
                untilDestroyed(this),
            )
            .subscribe((autoFilledAnnualReport: IAnnualReport) =>
                this.handleAutoFilledAnnualReport(autoFilledAnnualReport),
            );

        this.annualReportProvider
            // REV-20899 save-side isolation: this whole-report object is persisted
            // verbatim on save, so it MUST be the RAW report. The bridged
            // finStatements would zero a v1 report's inherited legacy reclasses.
            // The DISPLAY finStatements (withLatestFrom below) stay reclass-aware,
            // so fixed assets values still include reclassifications.
            .getActiveAnnualReport$()
            .pipe(
                withLatestFrom(this.annualReportProvider.getActiveFinStatementsWithReclasses$()),
                map(([activeAnnualReport, finStatements]: [IAnnualReport, IFinStatements[]]) => {
                    if (
                        activeAnnualReport != null &&
                        activeAnnualReport.generalInformation != null &&
                        finStatements.length >= templateConstants.financialStatementMinimumLength &&
                        finStatements[templateConstants.balanceTemplate].templateAndAccountsMapping.template != null &&
                        finStatements[templateConstants.incomeTemplate].templateAndAccountsMapping.template != null
                    ) {
                        return [activeAnnualReport, finStatements];
                    } else {
                        // Return null, so that we we this state can be handeled
                        return [null, finStatements];
                    }
                }),

                untilDestroyed(this),
            )
            .subscribe(([activeAnnualReport, finStatements]: [IAnnualReport, IFinStatements[]]) => {
                this.handleGetActiveAnnualReport(activeAnnualReport, finStatements);
            });

        this.actionsListener$
            .pipe(ofType(onUpdateAnnualReportSuccessfully), untilDestroyed(this))
            .subscribe(() => this.onSaveSuccessfully());
        this.actionsListener$
            .pipe(ofType(onUpdateAnnualReportFailed), untilDestroyed(this))
            .subscribe(() => this.onSaveFailed());

        super.ngOnInit();
    }

    private handleAutoFilledAnnualReport(annualReport: IAnnualReport) {
        this.fixedAssetsSchedulePreviousYear = cloneDeep<FixedAssetsSchedule>(annualReport.fixedAssetsSchedule);
    }

    private handleGetActiveAnnualReport(
        currentAnnualReport: IAnnualReport | null,
        finStatements: IFinStatements[],
    ): void {
        if (currentAnnualReport == null) {
            this.dataState = DataState.Error;
            this.showCompleteDataErrorMessage = true;
            return;
        }
        if (
            currentAnnualReport == null ||
            currentAnnualReport.generalInformation == null ||
            finStatements.length < templateConstants.financialStatementMinimumLength ||
            finStatements[templateConstants.balanceTemplate].templateAndAccountsMapping.template == null ||
            finStatements[templateConstants.incomeTemplate].templateAndAccountsMapping.template == null
        ) {
            this.dataState = DataState.Error;
            throw new Error(
                'current annual report and general information and balance template and income template must not be equal null',
            );
        }

        this.showCompleteDataErrorMessage = false;

        this.currentAnnualReport = cloneDeep<IAnnualReport>(currentAnnualReport);
        this.reportLocale = this.currentAnnualReport.locale.toLowerCase();

        this.fixedAssetsScheduleSavedData = cloneDeep<FixedAssetsSchedule | null>(
            currentAnnualReport.fixedAssetsSchedule,
        );

        this.mappedTemplate = finStatements[templateConstants.balanceTemplate].templateAndAccountsMapping.template;

        this.noPreviousData = PreviousPeriodHelper.hasNoPreviousPeriod(currentAnnualReport.generalInformation);
        this.periodStartDate = this.noPreviousData
            ? PreviousPeriodHelper.toDate(currentAnnualReport.generalInformation.incomeCurrentYearFromDate)
            : undefined;

        this.balanceOpeningDate = PreviousPeriodHelper.toDate(
            currentAnnualReport.generalInformation.balancePreviousYearDate,
        );

        const balanceClosingDate = new Date(currentAnnualReport.generalInformation.balanceSheetDate);
        this.balanceClosingDate = balanceClosingDate;

        this.dataState = DataState.Success;
    }

    onFixedAssetsScheduleOutputChange(fixedAssetsSchedule: FixedAssetsSchedule): void {
        this.fixedAssetsScheduleToSave = cloneDeep<FixedAssetsSchedule>(fixedAssetsSchedule);
    }

    proceedButtonClicked = (): void => {
        this.save();
    };

    override save(): Observable<void> {
        this.mixpanelService.track(MIXPANEL_EVENTS.FAS_TIME_SPENT_TILL_COMPLETION);

        if (this.currentAnnualReport == null) {
            throw new Error('Current annual is null!');
        }

        const annualReportToSave = cloneDeep<IAnnualReport>(this.currentAnnualReport);

        annualReportToSave.fixedAssetsSchedule = cloneDeep<FixedAssetsSchedule | null>(this.fixedAssetsScheduleToSave);

        this.isSaveRequestInProgress = true;
        this.dispatchSaveAnnualReport(annualReportToSave);

        return of(void 0);
    }

    protected onSaveSuccessfully(): void {
        if (this.isSaveRequestInProgress) {
            this.isSaveRequestInProgress = false;
            this.currentAnnualReport.fixedAssetsSchedule = cloneDeep<FixedAssetsSchedule>(this.fixedAssetsScheduleToSave);
            this.savedSuccessfully.next(true);
            this.fireSuccessNotification();
        }
    }

    private onSaveFailed(): void {
        this.isSaveRequestInProgress = false;
        this.savedSuccessfully.next(false);
        this.fireErrorMessage();
    }

    protected override isDataNewlyCreatedAndSaveable(): boolean {
        if (this.currentAnnualReport && this.currentAnnualReport.fixedAssetsSchedule == null && this.disableProceedButton === false) {
            return true;
        }
        return false;
    }

    protected override isDataChanged(): Observable<boolean> {
        if (this.currentAnnualReport == null) {
            return of(false);
        }
        if (this.isSaveRequestInProgress) {
            return this.savedSuccessfully.pipe(map((savedSuccessfully) => !savedSuccessfully));
        } else {
            const isCurrentEqualSavedEquityMovementSchedule = fastDeepEqual(
                this.currentAnnualReport.fixedAssetsSchedule,
                this.fixedAssetsScheduleToSave,
            );
            return of(!isCurrentEqualSavedEquityMovementSchedule);
        }
    }
}
